// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@guestsupply/App/Components/Common/XngZone";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { Typography } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";

interface SignInPageLayoutStyles {
    container?: InjectableCss;
    backToCart?: LinkPresentationProps;
}

export const signInPageLayout: SignInPageLayoutStyles = {};

const styles = signInPageLayout;

const mapStateToProps = (state: ApplicationState) => {
    const accountSettings = getSettingsCollection(state).accountSettings;
    const returnUrl = getReturnUrl(state);
    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const hideBreadcrumbs = getCurrentPage(state).generalFields.hideBreadcrumbs;
    const cartUrl = getPageLinkByPageType(state, "CartPage")?.url || "/Cart";
    return {
        allowGuestCheckout: accountSettings.allowGuestCheckout && !getCurrentUserIsGuest(state) && referredFromShipping,
        hideBreadcrumbs,
        cartUrl,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const SignInPageLayout: FC<Props> = ({ id, allowGuestCheckout, hideBreadcrumbs, cartUrl }) => {
    const { isEditing } = useShellContext();
    return (
        <>
            {allowGuestCheckout && (
                <Link {...styles.backToCart} href={cartUrl}>
                    {translate("Back to Cart")}
                </Link>
            )}
            <StyledWrapper
                {...styles.container}
                data-test-selector="SignInPageLayout"
                className={
                    allowGuestCheckout ? "checkout-guest-container" : hideBreadcrumbs ? "hidden-breadcrumbs" : ""
                }
            >
                <StyledWrapper className="signInForm">
                    <XngZone contentId={id} zoneName="Content00" hint="SignInExistingAccount" />
                </StyledWrapper>
                <Typography as="div" className="line-seperator seperator1"></Typography>
                <StyledWrapper className="createAccount">
                    <XngZone contentId={id} zoneName="Content01" hint="CreateAccount" />
                </StyledWrapper>
                {(allowGuestCheckout || isEditing) && (
                    <>
                        <Typography
                            as="div"
                            className="line-seperator seperator2"
                            data-test-selector="checkout-guest-seperator"
                        />
                        <StyledWrapper className="guestCheckout">
                            <XngZone contentId={id} zoneName="Content02" hint="CheckoutAsGuest" />
                        </StyledWrapper>
                    </>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(SignInPageLayout),
    definition: {
        group: "Layout" as any,
        displayName: "SignIn page layout",
        allowedContexts: ["SignInPage"],
    },
};

export default widgetModule;
